/**
 * The sign-in form, shown when the deployment signs in with a password and nobody is signed in.
 *
 * It is deliberately the whole screen rather than a modal over the app: there is nothing behind
 * it to look at, and a form floating over a shell full of skeletons reads as a session that has
 * expired mid-task rather than one that never started.
 */

import { useState } from 'react';
import { Button } from '~/ui/Button';
import { TextInput } from '~/ui/Field';
import { ValidationMessages } from '~/ui/Feedback';
import { apiFetch, ApiError } from '~/api/client';
import { routes } from '~/lib/routes';
import { writeSession } from './jwt';
import styles from './SignIn.module.css';

interface LoginResponse {
  token: string;
  expiresIn: number;
  email: string;
  displayName: string;
}

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);

  const submit = async () => {
    setProblems([]);
    setPending(true);
    try {
      const result = await apiFetch<LoginResponse>(routes.api.login(), {
        method: 'POST',
        body: { email: email.trim(), password },
      });
      writeSession({
        token: result.data.token,
        email: result.data.email,
        displayName: result.data.displayName,
        expiresAt: Date.now() + result.data.expiresIn * 1000,
      });
      onSignedIn();
    } catch (error) {
      setProblems(
        error instanceof ApiError
          ? [error.message]
          : ['Could not reach the server. Try again in a moment.'],
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <main className={styles.screen}>
      <form
        className={styles.card}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <h1 className={styles.title}>The Law</h1>
        <p className={styles.blurb}>
          Sign in to review findings. Your decisions are recorded against your name, so the next
          review can read them.
        </p>

        <label htmlFor="signin-email" className={styles.label}>
          Email
        </label>
        <TextInput
          id="signin-email"
          value={email}
          type="email"
          autoComplete="username"
          aria-label="Email"
          onChange={(event) => setEmail(event.currentTarget.value)}
        />

        <label htmlFor="signin-password" className={styles.label}>
          Password
        </label>
        <TextInput
          id="signin-password"
          value={password}
          type="password"
          autoComplete="current-password"
          aria-label="Password"
          onChange={(event) => setPassword(event.currentTarget.value)}
        />

        <ValidationMessages tone="error" title="Could not sign in" messages={problems} />

        <Button
          variant="primary"
          type="submit"
          pending={pending}
          inactive={email.trim().length === 0 || password.length === 0}
        >
          Sign in
        </Button>
      </form>
    </main>
  );
}
